import ExcelJS from 'exceljs';

// ─── Types ───────────────────────────────────────────────────────

export interface ExportColumn<T> {
  header: string;
  /** Accessor: key of T or custom function */
  value: keyof T | ((row: T) => string | number | boolean | null | undefined);
  /** Optional number format for XLS (e.g. '#,##0.00') */
  numFmt?: string;
}

export type ExportFormat = 'csv' | 'xlsx';

export interface ExportOptions {
  /** Delimiter for CSV (default: ';') */
  delimiter?: string;
  /** Use FR number format for CSV: comma as decimal separator (default: true) */
  frNumbers?: boolean;
  /** Max rows to export — guard against huge exports (default: 50_000) */
  maxRows?: number;
  /** Sheet name for XLSX (default: 'Export') */
  sheetName?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────

function resolveValue<T>(row: T, col: ExportColumn<T>): string | number | boolean | null | undefined {
  if (typeof col.value === 'function') return col.value(row);
  return row[col.value] as string | number | boolean | null | undefined;
}

function formatCsvCell(value: unknown, frNumbers: boolean): string {
  if (value == null) return '';
  const str = String(value);
  const formatted = frNumbers && typeof value === 'number' ? str.replace('.', ',') : str;
  // Escape quotes and wrap if contains delimiter, quote, or newline
  if (formatted.includes('"') || formatted.includes(';') || formatted.includes(',') || formatted.includes('\n')) {
    return `"${formatted.replace(/"/g, '""')}"`;
  }
  return formatted;
}

// ─── CSV Export ──────────────────────────────────────────────────

function buildCsv<T>(data: T[], columns: ExportColumn<T>[], opts: ExportOptions): string {
  const sep = opts.delimiter ?? ';';
  const fr = opts.frNumbers ?? true;
  const header = columns.map(c => formatCsvCell(c.header, false)).join(sep);
  const rows = data.map(row => columns.map(col => formatCsvCell(resolveValue(row, col), fr)).join(sep));
  return [header, ...rows].join('\n');
}

function downloadCsvBlob(csv: string, filename: string) {
  // BOM UTF-8 for Excel compatibility
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
}

// ─── XLSX Export ─────────────────────────────────────────────────

async function buildXlsx<T>(data: T[], columns: ExportColumn<T>[], opts: ExportOptions): Promise<ArrayBuffer> {
  const sheetName = opts.sheetName ?? 'Export';
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName);

  // Headers
  const headerRow = ws.addRow(columns.map(c => c.header));
  headerRow.font = { bold: true };

  // Data rows
  const resolvedRows = data.map(row => columns.map(col => resolveValue(row, col) ?? ''));
  for (const row of resolvedRows) {
    ws.addRow(row);
  }

  // Apply number formats if specified
  columns.forEach((col, colIdx) => {
    if (!col.numFmt) return;
    const excelCol = ws.getColumn(colIdx + 1);
    excelCol.numFmt = col.numFmt;
  });

  // Auto-width columns based on header + first 50 rows
  columns.forEach((col, colIdx) => {
    let max = col.header.length;
    const sampleSize = Math.min(data.length, 50);
    for (let i = 0; i < sampleSize; i++) {
      const val = resolvedRows[i]?.[colIdx];
      const len = val != null ? String(val).length : 0;
      if (len > max) max = len;
    }
    ws.getColumn(colIdx + 1).width = Math.min(max + 2, 50);
  });

  const buffer = await wb.xlsx.writeBuffer();
  return buffer as ArrayBuffer;
}

// ─── Trigger download ────────────────────────────────────────────

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Public API ──────────────────────────────────────────────────

export async function exportData<T>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string,
  format: ExportFormat = 'csv',
  options: ExportOptions = {},
): Promise<void> {
  const maxRows = options.maxRows ?? 50_000;
  const capped = data.length > maxRows ? data.slice(0, maxRows) : data;

  if (format === 'xlsx') {
    const buffer = await buildXlsx(capped, columns, options);
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const xlsFilename = filename.replace(/\.csv$/i, '') + '.xlsx';
    triggerDownload(blob, xlsFilename);
  } else {
    const csv = buildCsv(capped, columns, options);
    const csvFilename = filename.endsWith('.csv') ? filename : filename + '.csv';
    downloadCsvBlob(csv, csvFilename);
  }
}

/** Convenience: generate dated filename like `organisateurs-2026-03-20.csv` */
export function datedFilename(prefix: string, ext: ExportFormat = 'csv'): string {
  const date = new Date().toISOString().slice(0, 10);
  return `${prefix}-${date}.${ext}`;
}
